import { useCallback, useEffect, useMemo, useState } from 'react';
import { isPreviewableAudio } from './audioFormatUtils';
import { buildShuffleOrder, createTrackId, getNextTrackIndex, playlistEqGainsFromSource, playlistHasNextTrack } from './audioPlaylistStorage';

export function trackFromDeviceFile(file) {
  const name = file?.name || 'Audio track';
  return {
    trackId: createTrackId(),
    source: 'device',
    title: name.replace(/\.[^.]+$/, '') || name,
    fileName: name,
    mimeType: file?.type || '',
    objectUrl: URL.createObjectURL(file),
  };
}

export function trackFromOfflineRow(row) {
  return {
    trackId: createTrackId(),
    source: 'offline',
    offlineKey: row.key,
    title: row.title || 'Offline track',
    mimeType: row.mimeType || '',
  };
}

export function useLocalDevicePlaylist() {
  const [playlistName, setPlaylistName] = useState('On this device');
  const [tracks, setTracks] = useState([]);
  const [currentTrackId, setCurrentTrackId] = useState('');
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [eqGains, setEqGains] = useState(() => playlistEqGainsFromSource(null));
  const [playOrder, setPlayOrder] = useState([]);
  const [status, setStatus] = useState('');

  const currentIndex = useMemo(
    () => tracks.findIndex((t) => t.trackId === currentTrackId),
    [tracks, currentTrackId]
  );

  useEffect(() => {
    if (!tracks.length) {
      setPlayOrder([]);
      return;
    }
    const start = currentIndex >= 0 ? currentIndex : 0;
    setPlayOrder(shuffle ? buildShuffleOrder(tracks.length, start) : tracks.map((_, i) => i));
  }, [tracks.length, shuffle]); // eslint-disable-line react-hooks/exhaustive-deps

  const addFilesFromPicker = useCallback((fileList) => {
    const files = Array.from(fileList || []).filter((f) => isPreviewableAudio(f.type, f.name));
    if (!files.length) {
      setStatus('Choose MP3, M4A, WAV, or other supported audio files.');
      return;
    }
    const added = files.map((file) => trackFromDeviceFile(file));
    setTracks((prev) => {
      const next = [...prev, ...added];
      if (!currentTrackId && added[0]) setCurrentTrackId(added[0].trackId);
      return next;
    });
    setStatus(`Added ${added.length} track${added.length === 1 ? '' : 's'}.`);
  }, [currentTrackId]);

  const addOfflineTrack = useCallback(
    (row) => {
      if (!row?.key) return;
      setTracks((prev) => {
        const exists = prev.some((t) => t.source === 'offline' && t.offlineKey === row.key);
        if (exists) {
          const existing = prev.find((t) => t.source === 'offline' && t.offlineKey === row.key);
          if (existing) setCurrentTrackId(existing.trackId);
          setStatus('Track is already in the list.');
          return prev;
        }
        const track = trackFromOfflineRow(row);
        setCurrentTrackId(track.trackId);
        setStatus('Added offline track.');
        return [...prev, track];
      });
    },
    []
  );

  const advanceTrack = useCallback(() => {
    if (tracks.length <= 1) return;
    const nextIndex = getNextTrackIndex(tracks.length, currentIndex, playOrder, repeat);
    if (nextIndex == null) return;
    setCurrentTrackId(tracks[nextIndex]?.trackId || '');
  }, [tracks, playOrder, currentIndex, repeat]);

  const hasNextTrackInQueue = useMemo(
    () => playlistHasNextTrack(tracks.length, currentIndex, playOrder, repeat),
    [tracks.length, currentIndex, playOrder, repeat]
  );

  const loadTracks = useCallback((loaded, meta) => {
    setTracks((prev) => {
      prev.forEach((t) => {
        if (t.source === 'device' && t.objectUrl) URL.revokeObjectURL(t.objectUrl);
      });
      return loaded || [];
    });
    setCurrentTrackId(loaded?.[0]?.trackId || '');
    if (meta?.name) setPlaylistName(meta.name);
    if (typeof meta?.shuffle === 'boolean') setShuffle(meta.shuffle);
    if (typeof meta?.repeat === 'boolean') setRepeat(meta.repeat);
    setEqGains(playlistEqGainsFromSource(meta));
  }, []);

  const currentTrack = useMemo(
    () => tracks.find((t) => t.trackId === currentTrackId) || null,
    [tracks, currentTrackId]
  );

  return {
    playlistName,
    setPlaylistName,
    tracks,
    setTracks,
    currentTrackId,
    setCurrentTrackId,
    shuffle,
    setShuffle,
    repeat,
    setRepeat,
    eqGains,
    setEqGains,
    status,
    setStatus,
    addFilesFromPicker,
    addOfflineTrack,
    advanceTrack,
    loadTracks,
    currentTrack,
    hasNextTrackInQueue,
  };
}
